import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import RichTextEditor from '@/components/RichTextEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PenLine, Save, X, Plus, Loader2, ChevronLeft, Info } from 'lucide-react';

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function WriteStory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const isEditing = id ? true : false;
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [title, setTitle] = useState('');
  const [synopsis, setSynopsis] = useState('');
  const [content, setContent] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [coverImageDescription, setCoverImageDescription] = useState('');
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const catRes = await api.get('/categories');
        setCategories(catRes.data);

        if (isEditing) {
          const storyRes = await api.get('/stories/' + id);
          const s = storyRes.data;
          setTitle(s.title);
          setSynopsis(s.synopsis);
          setContent(s.content || '');
          setSelectedCategories(s.categories);
          setTags(s.tags);
          setCoverImageDescription(s.cover_image_description || '');
          setIsPremium(s.is_premium);
        }
      } catch (err) {
        if (isEditing) {
          toast.error('Erro ao carregar conto');
          navigate('/author');
        }
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id, isEditing, navigate]);

  function handleCategoryToggle(slug) {
    let updated = [];
    let found = false;
    for (let i = 0; i < selectedCategories.length; i++) {
      if (selectedCategories[i] === slug) {
        found = true;
      } else {
        updated.push(selectedCategories[i]);
      }
    }
    if (!found) {
      updated.push(slug);
    }
    setSelectedCategories(updated);
  }

  function handleAddTag() {
    const tag = tagInput.trim().toLowerCase();
    if (tag) {
      let exists = false;
      for (let i = 0; i < tags.length; i++) {
        if (tags[i] === tag) {
          exists = true;
          break;
        }
      }
      if (!exists) {
        setTags(tags.concat([tag]));
      }
    }
    setTagInput('');
  }

  function handleRemoveTag(tagToRemove) {
    const updated = [];
    for (let i = 0; i < tags.length; i++) {
      if (tags[i] !== tagToRemove) {
        updated.push(tags[i]);
      }
    }
    setTags(updated);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }
    if (!synopsis.trim()) {
      toast.error('Sinopse é obrigatória');
      return;
    }
    if (!stripHtml(content).trim()) {
      toast.error('Conteúdo é obrigatório');
      return;
    }
    if (selectedCategories.length === 0) {
      toast.error('Seleciona ao menos uma categoria');
      return;
    }

    setSaving(true);
    const formData = {
      title: title,
      synopsis: synopsis,
      content: content,
      categories: selectedCategories,
      tags: tags,
      cover_image_description: coverImageDescription,
      is_premium: isAdmin ? isPremium : false,
    };

    try {
      if (isEditing) {
        await api.put('/stories/' + id, formData);
        toast.success(
          isAdmin
            ? 'Conto atualizado com sucesso!'
            : 'Conto atualizado — voltou para aprovação da administração.'
        );
      } else {
        await api.post('/stories', formData);
        toast.success(
          isAdmin
            ? 'Conto publicado com sucesso!'
            : 'Conto enviado! Será publicado depois da aprovação da administração.'
        );
      }
      navigate('/author');
    } catch (err) {
      toast.error('Erro ao guardar conto');
    } finally {
      setSaving(false);
    }
  }

  const plainText = stripHtml(content);
  const words = plainText.split(/\s+/);
  let wordCount = 0;
  for (let i = 0; i < words.length; i++) {
    if (words[i]) wordCount++;
  }
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ruby" />
      </div>
    );
  }

  const categoryButtons = [];
  for (let i = 0; i < categories.length; i++) {
    const cat = categories[i];
    // Hide the "autores" category from non-admins — it's auto-applied to
    // every community submission, no need to expose it as a chooser.
    if (!isAdmin && cat.slug === 'autores') continue;
    let isSelected = false;
    for (let j = 0; j < selectedCategories.length; j++) {
      if (selectedCategories[j] === cat.slug) {
        isSelected = true;
        break;
      }
    }
    categoryButtons.push(
      <Button
        key={'cat-' + i}
        type="button"
        variant={isSelected ? "default" : "outline"}
        onClick={() => handleCategoryToggle(cat.slug)}
        data-testid={'category-' + cat.slug}
        className={'rounded-full ' + (isSelected ? 'bg-ruby hover:bg-ruby-light' : 'border-border')}
      >
        {cat.name}
      </Button>
    );
  }

  const tagBadges = [];
  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i];
    tagBadges.push(
      <Badge key={'tag-' + i} variant="secondary" className="bg-surface-light">
        #{tag}
        <button type="button" onClick={() => handleRemoveTag(tag)} className="ml-1 hover:text-red-400">
          <X className="w-3 h-3" />
        </button>
      </Badge>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-muted-foreground hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-1" />Voltar
            </Button>
          </div>

          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-ruby/10 flex items-center justify-center">
              <PenLine className="w-6 h-6 text-ruby" />
            </div>
            <div>
              <h1 className="font-brand text-2xl font-bold text-foreground">
                {isEditing ? 'Editar Conto' : 'Escrever Novo Conto'}
              </h1>
              <p className="text-muted-foreground">
                {isEditing ? 'Atualiza seu conto' : 'Compartilhe sua história com a comunidade'}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Um título cativante..."
                data-testid="write-title"
                className="bg-surface border-border h-12"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="synopsis">Sinopse *</Label>
              <Textarea
                id="synopsis"
                value={synopsis}
                onChange={(e) => setSynopsis(e.target.value)}
                placeholder="Uma breve descrição..."
                data-testid="write-synopsis"
                className="bg-surface border-border min-h-[100px]"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cover-image-description">
                Como imaginas a imagem de capa deste conto?
              </Label>
              <Textarea
                id="cover-image-description"
                value={coverImageDescription}
                onChange={(e) => setCoverImageDescription(e.target.value.slice(0, 1000))}
                maxLength={1000}
                placeholder="Ex.: Uma silhueta feminina à janela ao anoitecer, luz quente a atravessar cortinas de linho..."
                data-testid="write-cover-image-description"
                className="bg-surface border-border min-h-[120px]"
              />
              <p className="text-xs text-muted-foreground">
                Descreve a imagem que melhor representa a atmosfera, o ambiente, as personagens ou o momento-chave do teu conto. Esta descrição vai ajudar a equipa editorial da Intimate a criar ou selecionar a imagem de capa.
              </p>
              <p className="text-xs text-muted-foreground text-right">
                {coverImageDescription.length}/1000
              </p>
            </div>

            <div className="space-y-2">
              <Label>Categorias *</Label>
              <div className="flex flex-wrap gap-2">{categoryButtons}</div>
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                  placeholder="Adicionar tag..."
                  data-testid="write-tag-input"
                  className="bg-surface border-border h-12"
                />
                <Button type="button" onClick={handleAddTag} className="bg-ruby hover:bg-ruby-light">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {tags.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{tagBadges}</div>}
            </div>

            <div className="space-y-2">
              <Label>Conteúdo *</Label>
              <div data-testid="write-content">
                <RichTextEditor
                  content={content}
                  onChange={setContent}
                  placeholder="Escreve o seu conto aqui..."
                  minHeight="400px"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {wordCount} palavras • ~{readingTime} min de leitura
              </p>
            </div>

            <div className="flex items-center gap-3 p-4 bg-ruby/5 border border-ruby/30 rounded-lg" data-testid="community-submission-notice">
              <Info className="w-5 h-5 text-ruby shrink-0" />
              <p className="text-sm text-foreground">
                {isAdmin ? (
                  <>Estás a publicar como administradora — o conto fica publicado imediatamente.</>
                ) : (
                  <>O teu conto será publicado na categoria <strong>Autores</strong> assim que for aprovado pela administração. Todos os contos da comunidade são <strong>gratuitos</strong>.</>
                )}
              </p>
            </div>

            {isAdmin && (
              <div className="flex items-center justify-between p-4 bg-surface rounded-lg border border-border">
                <div>
                  <Label htmlFor="premium" className="text-foreground">Conteúdo Premium</Label>
                  <p className="text-sm text-muted-foreground">Apenas assinantes premium poderão ler</p>
                </div>
                <Switch
                  id="premium"
                  checked={isPremium}
                  onCheckedChange={setIsPremium}
                  data-testid="write-premium-toggle"
                />
              </div>
            )}

            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={saving}
                data-testid="write-submit"
                className="flex-1 bg-ruby hover:bg-ruby-light rounded-full py-6 font-semibold"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A guardar...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />{
                    isEditing
                      ? (isAdmin ? 'Atualizar Conto' : 'Atualizar e enviar para aprovação')
                      : (isAdmin ? 'Publicar Conto' : 'Enviar para aprovação')
                  }</>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

export default WriteStory;
